import { ITrainingSet } from '../../types/interfaces/training-set';
import { groupBy, orderBy } from '../collections/array-utils';

export function trainingSetsToBlocks(sets: ITrainingSet[]): ITrainingSet[][] {
  if (!sets?.length) return [];
  const groups = groupBy(sets, 'setNumber');
  const keys = Object.keys(groups)
    .map(Number)
    .sort((a, b) => a - b);
  return keys.map((k) => orderBy(groups[k], 'orderNumber'));
}

export function blocksToOrderPayload(
  blocks: ITrainingSet[][],
): { id: string; setNumber: number; orderNumber: number }[] {
  const result: { id: string; setNumber: number; orderNumber: number }[] = [];
  blocks.forEach((block, setNumber) => {
    block.forEach((set, orderNumber) => {
      if (set.id) {
        result.push({ id: set.id, setNumber, orderNumber });
      }
    });
  });
  return result;
}

export function moveRowToBetweenBlocks(
  blocks: ITrainingSet[][],
  fromBlock: number,
  fromRow: number,
  insertBeforeBlockIndex: number,
): ITrainingSet[][] {
  const nb = blocks.map((b) => [...b]);
  const [removed] = nb[fromBlock].splice(fromRow, 1);
  let insertAt = insertBeforeBlockIndex;
  if (nb[fromBlock].length === 0) {
    nb.splice(fromBlock, 1);
    if (fromBlock < insertBeforeBlockIndex) {
      insertAt -= 1;
    }
  }
  nb.splice(insertAt, 0, [removed]);
  return nb;
}

export function moveRowToBlock(
  blocks: ITrainingSet[][],
  fromBlock: number,
  fromRow: number,
  toBlock: number,
  toRow: number,
): ITrainingSet[][] {
  const nb = blocks.map((b) => [...b]);
  if (fromBlock === toBlock) {
    const row = nb[fromBlock][fromRow];
    if (fromRow === toRow) {
      return blocks.map((b) => [...b]);
    }
    nb[fromBlock].splice(fromRow, 1);
    let insertRow = toRow;
    if (fromRow < toRow) {
      insertRow -= 1;
    }
    nb[fromBlock].splice(insertRow, 0, row);
    return nb;
  }
  const [removed] = nb[fromBlock].splice(fromRow, 1);
  let tB = toBlock;
  if (nb[fromBlock].length === 0) {
    nb.splice(fromBlock, 1);
    if (fromBlock < toBlock) {
      tB -= 1;
    }
  }
  nb[tB].splice(toRow, 0, removed);
  return nb;
}

export function mergeRowIntoBlockEnd(
  blocks: ITrainingSet[][],
  fromBlock: number,
  fromRow: number,
  targetB: number,
): ITrainingSet[][] {
  const toRow = blocks[targetB].length;
  return moveRowToBlock(blocks, fromBlock, fromRow, targetB, toRow);
}

export function moveBlockBetween(
  blocks: ITrainingSet[][],
  fromBlock: number,
  insertBeforeBlockIndex: number,
): ITrainingSet[][] {
  const nb = blocks.map((b) => [...b]);
  const [removed] = nb.splice(fromBlock, 1);
  let insertAt = insertBeforeBlockIndex;
  if (fromBlock < insertBeforeBlockIndex) {
    insertAt -= 1;
  }
  nb.splice(insertAt, 0, removed);
  return nb;
}

export function mergeBlocks(
  blocks: ITrainingSet[][],
  fromBlock: number,
  targetB: number,
): ITrainingSet[][] {
  if (fromBlock === targetB) {
    return blocks.map((b) => [...b]);
  }
  const nb = blocks.map((b) => [...b]);
  const [fromBlockSets] = nb.splice(fromBlock, 1);
  let tB = targetB;
  if (fromBlock < targetB) {
    tB -= 1;
  }
  nb[tB] = [...nb[tB], ...fromBlockSets];
  return nb;
}
